/* eslint-disable max-lines */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form, FormInstance } from 'antd';
import lookie from 'lookie';
import { useLatest, useMount, useSize } from 'ahooks';
import { omit } from 'lodash-es';
import { OrientationType, ScreenOrientation } from '@capawesome/capacitor-screen-orientation';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import localeData from 'dayjs/plugin/localeData';
import { Device, DeviceInfo } from '@capacitor/device';
import { Dialog } from 'antd-mobile';
import { Keyboard } from '@capacitor/keyboard';
import useNav from '@/hooks/useNav';
import { setCurrentCustomer } from '@/store/customer';
import { selectDevicePlatform, selectedStoreInfo } from '@/store/device';
import { Header } from '@/components';
import service from '@/service';
import { Toast } from '@/utils';
import { ALL_PAGES, JUDGE_AS_VERTICAL_SCREEN_HEIGHT, SELECTED_CUSTOMER_ID, UPPER_STATUS_CODE } from '@/constant';
import { NameStep } from '../components/NameStep';
import { BirthdayStep } from '../components/BrithdayStep';
import { PhoneStep } from '../components/PhoneStep';
import HeaderMask from '../components/HeaderMask';
import ScreenFlipTip from '../../search-member/screen-flip-tip';

import cx from './index.module.scss';

interface Props {}

dayjs.extend(weekday);
dayjs.extend(localeData);
const validateMessages = {
  // eslint-disable-next-line no-template-curly-in-string
  required: '${label}不能为空',
};

export const CODE_NEXT_SEND_TIME = 'add_member_code_next_send_time';
const DateFormat = 'YYYY/MM/DD';

/**
 * 通过生日计算年龄
 */
export const getAgeFromBirthday = (dateString: string): number => {
  const birthday = new Date(dateString);
  const ageDifMs = Date.now() - birthday.getTime();
  const ageDate = new Date(ageDifMs);
  return Math.abs(ageDate.getUTCFullYear() - 1970);
};

export enum AddMemberStep {
  Name,
  Phone,
  Birthday,
}

export interface StepCompProps {
  form: FormInstance<any>;
  hidden: boolean;
  className?: string;
  hasHopeType?: boolean;
}

const AddMember: React.FC<Props> = () => {
  const sizeRef = useRef(null);
  const screenSize = useSize(sizeRef);
  const [isTipOpen, setIsTipOpen] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState(AddMemberStep.Phone);
  const [form] = Form.useForm();
  const location = useLocation();
  const dispatch = useDispatch();
  const storeInfo = useSelector(selectedStoreInfo);
  const lastStoreInfo = useLatest<StoreInfo>(storeInfo);
  const currentOpPlatform = useRef<DeviceInfo['platform']>(null);
  const [isKeyBoardShow, setIsKeyBoardShow] = useState(false);
  const devicePlatform = useSelector(selectDevicePlatform);
  const initListener = useCallback(async () => {
    currentOpPlatform.current = (await Device.getInfo())?.platform;
    if (currentOpPlatform.current === 'android') {
      Keyboard.addListener('keyboardWillShow', () => {
        setIsKeyBoardShow(true);
      });
      Keyboard.addListener('keyboardWillHide', () => {
        setIsKeyBoardShow(false);
      });
    }
  }, []);

  const nav = useNav();
  const isVerticalScreen = useMemo(() => screenSize?.height > JUDGE_AS_VERTICAL_SCREEN_HEIGHT, [screenSize?.height]);

  const isShowBackConfirm = useRef(false);

  const handleSave = useCallback(() => {
    form.submit();
  }, [form]);

  const handleNextStep = async () => {
    const formNameMap = {
      [AddMemberStep.Phone]: ['phoneNumber', 'code'],
      [AddMemberStep.Name]: ['name'],
      [AddMemberStep.Birthday]: ['birthday', 'source'],
    };

    await form.validateFields(formNameMap[currentStep]);
    setCurrentStep(currentStep + 1);

    if (currentStep === AddMemberStep.Birthday) {
      console.warn('已经是最后一步了');
      return;
    }
  };

  const handleLastStep = () => {
    if (currentStep === AddMemberStep.Phone) {
      return;
    }
    setCurrentStep(currentStep - 1);
  };

  useMount(() => {
    storeInfo?.phoneNumberCheck === 0 && setCurrentStep(AddMemberStep.Birthday);
  });

  const backConfirm = useCallback(() => {
    if (isShowBackConfirm.current) return;
    isShowBackConfirm.current = true;
    Dialog.confirm({
      destroyOnClose: true,
      title: `是否要放弃添加会员？`,
      content: '退出后已填写的内容将不会保留',
      confirmText: '确认',
      onConfirm: () => {
        isShowBackConfirm.current = false;
        nav(ALL_PAGES.HOME);
      },
      onCancel: () => {
        isShowBackConfirm.current = false;
      },
    });
  }, [nav]);

  const handleFormFinish = useCallback(
    async fields => {
      fields.birthday = fields.birthday?.format(DateFormat);
      fields.age = getAgeFromBirthday(fields.birthday);
      try {
        Toast.loading('正在保存', { maskClickable: false, duration: 0 });
        const { data } = await service.customer.createCustomer(fields);

        const { data: customerInfo } = await service.customer.getCustomerProfile(data?.customerId);
        dispatch(setCurrentCustomer(customerInfo));
        lookie.set(SELECTED_CUSTOMER_ID, data?.customerId);
        Toast.success('保存成功');

        if (location?.state?.action === 'ai-consultation') {
          dispatch(
            setCurrentCustomer(
              omit({ ...customerInfo, isVerticalScreen, isTipOpen, setIsTipOpen }, ['isTipOpen', 'setIsTipOpen'])
            )
          );
          if (devicePlatform === 'android') {
            // TODO  显示UI 提示跳转
            setTimeout(() => ScreenOrientation.lock({ type: OrientationType.PORTRAIT_PRIMARY }), 0);
            setIsTipOpen(true);
          } else {
            nav(ALL_PAGES.AI_CONSULTATION);
          }
        } else {
          nav(ALL_PAGES.ADD_SERVICE);
        }
      } catch ({ code, message }) {
        if (code === UPPER_STATUS_CODE.SmsCodeError) {
          Toast.fail(message || '请输入正确的验证码');
          return;
        }
        Toast.fail(message || '保存失败');
      }
    },
    [devicePlatform, dispatch, isTipOpen, isVerticalScreen, location?.state?.action, nav]
  );

  useEffect(() => {
    initListener();
    return () => {
      if (currentOpPlatform.current === 'android') {
        Keyboard.removeAllListeners();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isTipOpen && isVerticalScreen && devicePlatform === 'android') {
      nav(ALL_PAGES.AI_CONSULTATION, {
        state: {
          action: 'member-detail',
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVerticalScreen, isTipOpen, devicePlatform]);

  return (
    <div className={cx('add-member')} ref={sizeRef}>
      <Header title={'添加会员'} backConfig={{ action: backConfirm, text: '首页' }} />
      <div className={cx('bg-mirror')} />
      <div className={cx('wrapper-box')}>
        <div
          className={cx('wrapper', {
            'form-phone': currentStep === 1,
            'form-others-info': currentStep === 2,
            'keyboard-active': isKeyBoardShow,
          })}
        >
          <div className={cx('top-bar', { 'flex-end': lastStoreInfo.current?.phoneNumberCheck === 0 })}>
            {lastStoreInfo.current?.phoneNumberCheck === 1 && (
              <span onClick={handleLastStep}>{currentStep !== AddMemberStep.Phone && '上一步'}</span>
            )}

            {currentStep === AddMemberStep.Birthday ? (
              <span className={cx('selected')} onClick={handleSave}>
                保存并新建
              </span>
            ) : (
              <span className={cx('selected')} onClick={handleNextStep}>
                下一步
              </span>
            )}
          </div>
          <div className={cx('form-wrapper')}>
            <Form
              onFinish={handleFormFinish}
              form={form}
              className={cx('form')}
              validateMessages={validateMessages}
              initialValues={{
                birthday: dayjs('2000/01/01'),
                age: getAgeFromBirthday('2000/01/01'),
                gender: 'Female',
              }}
            >
              <NameStep form={form} hidden={currentStep !== AddMemberStep.Birthday} />
              <PhoneStep
                form={form}
                isShowCode={lastStoreInfo.current?.phoneNumberCheck === 1}
                isValidPhone={lastStoreInfo.current?.phoneNumberCheck === 1}
                hidden={
                  currentStep !==
                  (lastStoreInfo.current?.phoneNumberCheck === 1 ? AddMemberStep.Phone : AddMemberStep.Birthday)
                }
              />
              <BirthdayStep form={form} hidden={currentStep !== AddMemberStep.Birthday} />
            </Form>
          </div>
        </div>

        <HeaderMask show={isKeyBoardShow} />
      </div>
      <ScreenFlipTip open={isTipOpen} setOpen={setIsTipOpen} />
    </div>
  );
};

export default AddMember;
